import React, { useState, useCallback, useEffect } from 'react';
import PatientInfo from './PatientInfo';
import { useUltrasoundForm } from '../hooks/useUltrasoundForm';
import DraftRestoreBanner from './DraftRestoreBanner';
import { useTechnologists } from '../hooks/useTechnologists';

const VEIN_STATUS = [
  'Normal - Compressible, patent',
  'Acute DVT',
  'Chronic DVT',
  'Acute & Chronic DVT',
  'Partial thrombosis',
  'Not well visualized'
];

function DVTForm({ token, onBack, facilityInfo }) {
  // Tab state: 0=Right Lower Extremity, 1=Left Lower Extremity
  const [activeTab, setActiveTab] = useState(0);
  // Fetch dynamic technologists list
  const { technologists, defaultTech } = useTechnologists();

  const INITIAL_DVT_DATA = {
    examType: 'DVT',
    examDate: new Date().toISOString().split('T')[0],
    firstName: '',
    lastName: '',
    mrn: '',
    technologist: '',
    reason: '',
    callReport: false,
    phoneNumber: '',
    history: '',
    comments: '',
    rightLegEvaluated: false,
    leftLegEvaluated: false,
    rightLeg: {
      commonFemoralVein: {
        status: 'Normal - Compressible, patent',
        notes: ''
      },
      deepFemoralVein: {
        status: 'Normal - Compressible, patent',
        notes: ''
      },
      superficialFemoralVein: {
        status: 'Normal - Compressible, patent',
        notes: ''
      },
      poplitealVein: {
        status: 'Normal - Compressible, patent',
        notes: ''
      },
      posteriorTibialVeins: {
        status: 'Normal - Compressible, patent',
        notes: ''
      },
      peronealVeins: {
        status: 'Normal - Compressible, patent',
        notes: ''
      },
      greatSaphenousVein: {
        status: 'Normal - Compressible, patent',
        notes: ''
      }
    },
    leftLeg: {
      commonFemoralVein: {
        status: 'Normal - Compressible, patent',
        notes: ''
      },
      deepFemoralVein: {
        status: 'Normal - Compressible, patent',
        notes: ''
      },
      superficialFemoralVein: {
        status: 'Normal - Compressible, patent',
        notes: ''
      },
      poplitealVein: {
        status: 'Normal - Compressible, patent',
        notes: ''
      },
      posteriorTibialVeins: {
        status: 'Normal - Compressible, patent',
        notes: ''
      },
      peronealVeins: {
        status: 'Normal - Compressible, patent',
        notes: ''
      },
      greatSaphenousVein: {
        status: 'Normal - Compressible, patent',
        notes: ''
      }
    }
  };

  const {
    formData,
    error,
    success,
    isLoading,
    handleSubmit: hookHandleSubmit,
    handlePreview,
    updateField,
    updateNestedField,
    resetForm,
    clearMessages,
    fieldErrors,
    draftBannerProps
  } = useUltrasoundForm(INITIAL_DVT_DATA, 'DVT', {
    token, facilityInfo
  });

  // Update technologist field when defaultTech loads
  useEffect(() => {
    if (defaultTech && !formData.technologist) {
      updateField('technologist', defaultTech);
    }
  }, [defaultTech, formData.technologist, updateField]);

  const handleFormSubmit = useCallback(async (e) => {
    e.preventDefault();
    await hookHandleSubmit(e);
  }, [hookHandleSubmit]);

  const handleVeinChange = (leg, vein, field, value) => {
    // Get current leg data
    const currentLegData = formData[leg];
    // Update the specific vein's field
    const updatedLegData = {
      ...currentLegData,
      [vein]: {
        ...currentLegData[vein],
        [field]: value
      }
    };
    // Update the entire leg data
    updateField(leg, updatedLegData);
  };

  const handleLegEvaluationChange = (leg, evaluated) => {
    updateField(`${leg}Evaluated`, evaluated);
  };

  const renderVeinSection = (leg, legLabel, veinKey, veinLabel, isEvaluated) => {
    const veinData = formData[leg][veinKey];
    const isAbnormal = veinData.status !== 'Normal - Compressible, patent';

    return (
      <div className="form-group" style={{ marginBottom: '15px', opacity: isEvaluated ? 1 : 0.5 }}>
        <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '5px' }}>
          {veinLabel}:
        </label>
        <select
          value={veinData.status}
          onChange={(e) => handleVeinChange(leg, veinKey, 'status', e.target.value)}
          disabled={!isEvaluated}
          style={{ 
            width: '100%', 
            marginBottom: '5px',
            backgroundColor: !isEvaluated ? 'var(--color-surface)' : (isAbnormal ? 'var(--color-status-bad-soft)' : 'var(--color-input-bg)'),
            borderColor: isAbnormal ? 'var(--color-danger)' : 'var(--color-border)',
            cursor: isEvaluated ? 'pointer' : 'not-allowed'
          }}
        >
          {VEIN_STATUS.map(status => (
            <option key={status} value={status}>{status}</option>
          ))}
        </select>
        {isAbnormal && isEvaluated && (
          <input
            type="text"
            value={veinData.notes}
            onChange={(e) => handleVeinChange(leg, veinKey, 'notes', e.target.value)}
            placeholder="Additional notes..."
            style={{ width: '100%' }}
          />
        )}
      </div>
    );
  };

  return (
    <div className="form-container">
      <button onClick={onBack} className="back-btn" type="button">
        ← Back to Worksheet Selection
      </button>

      <form onSubmit={handleFormSubmit}>
        {/* Status Messages */}
        {error && (
          <div className="alert alert-danger" role="alert">
            <strong>Error:</strong> {error}
            <button 
              type="button"
              className="btn-close" 
              onClick={clearMessages}
              aria-label="Close"
            />
          </div>
        )}

        {success && (
          <div className="alert alert-success" role="alert">
            <strong>Success:</strong> {success}
            <button 
              type="button"
              className="btn-close" 
              onClick={clearMessages}
              aria-label="Close"
            />
          </div>
        )}

        {/* Draft restore banner */}
        {draftBannerProps && <DraftRestoreBanner {...draftBannerProps} />}

        {/* Patient Information */}
        <PatientInfo formData={formData} updateField={updateField} technologists={technologists} fieldErrors={fieldErrors} />

        {/* History */}
        <div className="form-section">
          <h3>History</h3>
          <div className="form-group">
            <label>Clinical History:</label>
            <input
              type="text"
              value={formData.history}
              onChange={(e) => updateField('history', e.target.value)}
              placeholder="Enter clinical history (e.g., leg swelling, pain, suspected DVT)"
            />
          </div>
        </div>

        {/* TAB NAVIGATION */}
        <div className="form-section">
          <div className="worksheet-tabs">
            <button
              type="button"
              onClick={() => setActiveTab(0)}
              className={`worksheet-tab-btn${activeTab === 0 ? ' active' : ''}`}
            >
              Right Lower Extremity
            </button>
            <button
              type="button"
              onClick={() => setActiveTab(1)}
              className={`worksheet-tab-btn${activeTab === 1 ? ' active' : ''}`}
            >
              Left Lower Extremity
            </button>
          </div>

          {/* Tab Content */}
          <div className="worksheet-tab-content" style={{ minHeight: '400px' }}>
            {/* Right Lower Extremity Tab */}
            {activeTab === 0 && (
              <div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '15px' }}>
                  <h3 style={{ margin: 0, color: 'var(--color-primary)' }}>Right Lower Extremity</h3>
            <div style={{ display: 'flex', gap: '20px', alignItems: 'center' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '5px', cursor: 'pointer' }}>
                <input
                  type="radio"
                  name="rightLegEvaluation"
                  checked={formData.rightLegEvaluated}
                  onChange={() => handleLegEvaluationChange('rightLeg', true)}
                />
                <span style={{ fontWeight: formData.rightLegEvaluated ? 'bold' : 'normal', color: formData.rightLegEvaluated ? 'var(--color-success)' : 'var(--color-text-secondary)' }}>
                  Evaluated
                </span>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '5px', cursor: 'pointer' }}>
                <input
                  type="radio"
                  name="rightLegEvaluation"
                  checked={!formData.rightLegEvaluated}
                  onChange={() => handleLegEvaluationChange('rightLeg', false)}
                />
                <span style={{ fontWeight: !formData.rightLegEvaluated ? 'bold' : 'normal', color: !formData.rightLegEvaluated ? 'var(--color-danger)' : 'var(--color-text-secondary)' }}>
                  Not Evaluated
                </span>
              </label>
            </div>
          </div>
          
          {renderVeinSection('rightLeg', 'Right', 'commonFemoralVein', 'Common Femoral Vein', formData.rightLegEvaluated)}
          {renderVeinSection('rightLeg', 'Right', 'deepFemoralVein', 'Deep Femoral Vein', formData.rightLegEvaluated)}
          {renderVeinSection('rightLeg', 'Right', 'superficialFemoralVein', 'Superficial Femoral Vein', formData.rightLegEvaluated)}
          {renderVeinSection('rightLeg', 'Right', 'poplitealVein', 'Popliteal Vein', formData.rightLegEvaluated)}
          {renderVeinSection('rightLeg', 'Right', 'posteriorTibialVeins', 'Posterior Tibial Veins', formData.rightLegEvaluated)}
          {renderVeinSection('rightLeg', 'Right', 'peronealVeins', 'Peroneal Veins', formData.rightLegEvaluated)}
          {renderVeinSection('rightLeg', 'Right', 'greatSaphenousVein', 'Great Saphenous Vein', formData.rightLegEvaluated)}
              </div>
            )}

            {/* Left Lower Extremity Tab */}
            {activeTab === 1 && (
              <div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '15px' }}>
                  <h3 style={{ margin: 0, color: 'var(--color-primary)' }}>Left Lower Extremity</h3>
            <div style={{ display: 'flex', gap: '20px', alignItems: 'center' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '5px', cursor: 'pointer' }}>
                <input
                  type="radio"
                  name="leftLegEvaluation"
                  checked={formData.leftLegEvaluated}
                  onChange={() => handleLegEvaluationChange('leftLeg', true)}
                />
                <span style={{ fontWeight: formData.leftLegEvaluated ? 'bold' : 'normal', color: formData.leftLegEvaluated ? 'var(--color-success)' : 'var(--color-text-secondary)' }}>
                  Evaluated
                </span>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '5px', cursor: 'pointer' }}>
                <input
                  type="radio"
                  name="leftLegEvaluation"
                  checked={!formData.leftLegEvaluated}
                  onChange={() => handleLegEvaluationChange('leftLeg', false)}
                />
                <span style={{ fontWeight: !formData.leftLegEvaluated ? 'bold' : 'normal', color: !formData.leftLegEvaluated ? 'var(--color-danger)' : 'var(--color-text-secondary)' }}>
                  Not Evaluated
                </span>
              </label>
            </div>
          </div>

          {renderVeinSection('leftLeg', 'Left', 'commonFemoralVein', 'Common Femoral Vein', formData.leftLegEvaluated)}
          {renderVeinSection('leftLeg', 'Left', 'deepFemoralVein', 'Deep Femoral Vein', formData.leftLegEvaluated)}
          {renderVeinSection('leftLeg', 'Left', 'superficialFemoralVein', 'Superficial Femoral Vein', formData.leftLegEvaluated)}
          {renderVeinSection('leftLeg', 'Left', 'poplitealVein', 'Popliteal Vein', formData.leftLegEvaluated)}
          {renderVeinSection('leftLeg', 'Left', 'posteriorTibialVeins', 'Posterior Tibial Veins', formData.leftLegEvaluated)}
          {renderVeinSection('leftLeg', 'Left', 'peronealVeins', 'Peroneal Veins', formData.leftLegEvaluated)}
          {renderVeinSection('leftLeg', 'Left', 'greatSaphenousVein', 'Great Saphenous Vein', formData.leftLegEvaluated)}
              </div>
            )}
          </div>
        </div>

        {/* Comments */}
        <div className="form-section">
          <h3>Comments</h3>
          <textarea
            rows="8"
            value={formData.comments}
            onChange={(e) => updateField('comments', e.target.value)}
            placeholder="Additional comments or impressions..."
            style={{ width: '100%', minHeight: '150px', fontSize: '16px' }}
          />
        </div>

        {/* Form Actions */}
        <div className="form-actions">
          <button
            type="button"
            onClick={handlePreview}
            disabled={isLoading}
            className="preview-btn"
          >
            Preview Report
          </button>
          <button 
            type="submit" 
            disabled={isLoading} 
            className="submit-btn"
          >
            {isLoading ? 'Generating Report...' : 'Generate & Save Report'}
          </button>
          <button 
            type="button"
            onClick={resetForm}
            disabled={isLoading}
            className="reset-btn"
          >
            Clear Form
          </button>
        </div>
      </form>
    </div>
  );
}

export default DVTForm;
